import { useCallback, useEffect } from 'react'

import { useMutation } from '@tanstack/react-query'

import { useCookieConsentPropsStore } from 'store/useCookieConsentProps'
import { useCookieConsentStore } from 'store/useCookieConsentStore'

import { saveCookieConsent } from 'services/cookieConsent'

import type {
  ICookieConsentRecord,
  TConsentByCategory,
  TCookieConsentByCategory
} from 'types/cookie-consent/cookie-consent.types'

import { getDeviceId } from 'utils/auth/deviceId'
import { LOCAL_STORAGE_KEYS } from 'utils/constants/common'
import { formatIsoWithOffset } from 'utils/cookieConsent'

/**
 * TCS-8180: cookie-consent persistence hook (LGPD/ANPD).
 *
 * Responsibilities:
 * - Read the persisted consent from localStorage and hydrate the store. A
 *   record is only valid while its policyVersion matches the version
 *   coming from the CMS (Liferay) — the business bumps that version whenever
 *   the policy changes, which invalidates the stored consent and shows the
 *   banner again. There is no FE-side version constant.
 * - Expose save handlers that write to localStorage and fire the (stubbed)
 *   consent API mutation. When the real API lands, the mutation already
 *   handles the request — only the service body changes.
 *
 * The store works with the UI format (booleans per category key); the
 * localStorage record and the API payload use the wire format
 * ('granted' | 'denied', ISO timestamp with offset, deviceId/ip). This hook
 * owns the conversions between the two.
 *
 * The consent category keys are dynamic (defined by the CMS cards' gtmName),
 * so the business can add/edit categories in Liferay without a FE change.
 *
 * NOTE: the consent record's `ip` field cannot be filled client-side — the
 * browser cannot read the user's public IP. Pending backend decision (the
 * consent API can derive it from the request headers server-side); until
 * then the record is persisted with an empty `ip`.
 */

/** UI format (booleans) -> wire format ('granted' | 'denied') */
function toWireConsent(consents: TCookieConsentByCategory): TConsentByCategory {
  return Object.fromEntries(
    Object.entries(consents).map(([key, granted]) => [
      key,
      granted ? 'granted' : 'denied'
    ])
  ) as TConsentByCategory
}

/** Wire format ('granted' | 'denied') -> UI format (booleans) */
function fromWireConsent(
  consent: TConsentByCategory
): TCookieConsentByCategory {
  return Object.fromEntries(
    Object.entries(consent).map(([key, value]) => [key, value === 'granted'])
  )
}

export function useCookieConsent() {
  const hasConsent = useCookieConsentStore.use.hasConsent()
  const consents = useCookieConsentStore.use.consents()
  const hydrateConsent = useCookieConsentStore.use.hydrateConsent()
  const clearConsent = useCookieConsentStore.use.clearConsent()
  const acceptAllStore = useCookieConsentStore.use.acceptAll()
  const refuseNonEssentialStore = useCookieConsentStore.use.refuseNonEssential()
  const saveConsentsStore = useCookieConsentStore.use.saveConsents()

  /** Cookie policy version defined by the business in the Liferay article */
  const cookiePolicyVersion =
    useCookieConsentPropsStore.use.cookiePolicyVersion()

  /** CMS cards (repeatable Liferay field, nested in the modal texts group) */
  const cookieCards =
    useCookieConsentPropsStore.use.configureCookiesModalTexts().cookieCards

  const saveConsentMutation = useMutation({
    mutationFn: saveCookieConsent
  })

  /** All consent keys (card gtmNames), from the CMS cards */
  const categoryKeys = cookieCards.map((card) => card.gtmName)

  /** Keys of the essential cards (always granted) */
  const essentialKeys = cookieCards
    .filter((card) => card.type === 'essential')
    .map((card) => card.gtmName)

  /**
   * Validate the persisted consent against the CMS cookie policy version and
   * hydrate the store. Re-runs whenever the CMS version changes (it arrives
   * asynchronously with the Liferay article):
   * - Record missing/malformed → nothing to hydrate (banner shows).
   * - CMS version known and different from the stored one → the consent is
   *   stale: drop the record and reset the store so the banner shows again.
   * - CMS version not available yet (empty) → hydrate optimistically; if the
   *   version arrives later and differs, this effect invalidates the consent.
   */
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT)

      if (!raw) {
        return
      }

      const record = JSON.parse(raw) as ICookieConsentRecord

      if (!record?.consent) {
        window.localStorage.removeItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT)

        return
      }

      /**
       * Stale consent: the business bumped the policy version in Liferay
       * after this record was saved.
       */
      if (cookiePolicyVersion && record.policyVersion !== cookiePolicyVersion) {
        window.localStorage.removeItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT)
        clearConsent()

        return
      }

      hydrateConsent(fromWireConsent(record.consent))
    } catch (error) {
      // Corrupted storage: ignore and let the banner decide
      console.log({ error })
    }
  }, [cookiePolicyVersion, hydrateConsent, clearConsent])

  /** Build the full record and persist it locally + server-side */
  const persistConsent = useCallback(
    async (newConsents: TCookieConsentByCategory) => {
      const deviceId = await getDeviceId()

      const record: ICookieConsentRecord = {
        deviceId,
        consent: toWireConsent(newConsents),
        acceptedAt: formatIsoWithOffset(),
        policyVersion: useCookieConsentPropsStore.getState().cookiePolicyVersion
      }

      try {
        window.localStorage.setItem(
          LOCAL_STORAGE_KEYS.COOKIE_CONSENT,
          JSON.stringify(record)
        )
      } catch (error) {
        console.log({ error })
      }

      // Fire-and-forget: the UI must not block on the consent API
      saveConsentMutation.mutate(record)
    },
    [saveConsentMutation]
  )

  const handleAcceptAll = useCallback(() => {
    acceptAllStore(categoryKeys)
    persistConsent(useCookieConsentStore.getState().consents)
  }, [acceptAllStore, persistConsent, categoryKeys])

  const handleRefuseNonEssential = useCallback(() => {
    refuseNonEssentialStore(categoryKeys, essentialKeys)
    persistConsent(useCookieConsentStore.getState().consents)
  }, [refuseNonEssentialStore, categoryKeys, essentialKeys, persistConsent])

  const handleSaveConsents = useCallback(
    (newConsents: TCookieConsentByCategory) => {
      saveConsentsStore(newConsents)
      persistConsent(useCookieConsentStore.getState().consents)
    },
    [saveConsentsStore, persistConsent]
  )

  return {
    hasConsent,
    consents,
    handleAcceptAll,
    handleRefuseNonEssential,
    handleSaveConsents
  }
}
